// src/components/DynamicCustomizationPage.jsx
// Special customization page for dynamic generation that doesn't rely on database templates

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, VStack, HStack, Heading, Text, Button, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import { useCESDK } from '../hooks/useCESDK';
import { generateDynamicScene } from '../utils/dynamicSceneGenerator';
import supabase from '../utils/supabaseUtils';

const DynamicCustomizationPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const initTimeoutRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generationData, setGenerationData] = useState(null);
  const [user, setUser] = useState(null);
  const [sceneGenerated, setSceneGenerated] = useState(false);
  const [initializationTimeout, setInitializationTimeout] = useState(false);

  // CESDK hook
  const {
    cesdk,
    status: cesdkStatus,
    initialize: initializeCESDK,
    createBlankScene,
    cleanup
  } = useCESDK();

  // Set up initialization timeout
  useEffect(() => {
    if (cesdkStatus === 'initializing') {
      // Set a 30-second timeout for initialization
      initTimeoutRef.current = setTimeout(() => {
        setInitializationTimeout(true);
        console.warn('⏰ CESDK initialization timeout after 30 seconds');
      }, 30000);
    } else {
      // Clear timeout if initialization completes
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      setInitializationTimeout(false);
    }

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [cesdkStatus]);

  // Retry initialization function
  const retryInitialization = () => {
    setInitializationTimeout(false);
    setError(null);
    if (editorRef.current && generationData && user) {
      console.log('🔄 Retrying CESDK initialization...');
      initializeCESDK(editorRef.current, {
        userId: user?.id,
        callbacks: {
          onUpload: 'local'
        }
      }).catch(err => {
        console.error('❌ CESDK retry initialization failed:', err);
        setError(`Editor initialization failed: ${err.message}`);
      });
    }
  };

  // Get user session and generation data
  useEffect(() => {
    const loadUserAndData = async () => {
      try {
        console.log('🔍 Loading user and generation data for sessionId:', sessionId);
        
        // Get current user
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          console.error('Authentication error:', sessionError);
          navigate('/login');
          return;
        }
        setUser(session.user);
        console.log('✅ User authenticated:', session.user.email);

        // Try to get generation data from multiple sources
        let data = null;
        let dataSource = null;

        // 1. Try React Router state first
        const routerState = window.history.state?.usr?.generationData;
        if (routerState) {
          console.log('📦 Found generation data in React Router state');
          data = routerState;
          dataSource = 'router_state';
        }

        // 2. Try sessionStorage
        if (!data) {
          const storedData = sessionStorage.getItem('dynamicGenerationData');
          if (storedData) {
            try {
              data = JSON.parse(storedData);
              dataSource = 'session_storage';
              console.log('📦 Found generation data in sessionStorage');
            } catch (parseError) {
              console.error('Failed to parse sessionStorage data:', parseError);
            }
          }
        }

        // 3. Try localStorage as backup
        if (!data) {
          const localData = localStorage.getItem('dynamicGenerationData');
          if (localData) {
            try {
              data = JSON.parse(localData);
              dataSource = 'local_storage';
              console.log('📦 Found generation data in localStorage');
            } catch (parseError) {
              console.error('Failed to parse localStorage data:', parseError);
            }
          }
        }

        // Validate the data
        if (!data) {
          console.error('❌ No generation data found in any source');
          setError('No generation data found. Please start from the demo page.');
          return;
        }

        console.log('📋 Generation data loaded from:', dataSource);
        console.log('📋 Data contents:', data);

        // Validate session ID match
        if (data.sessionId !== sessionId) {
          console.error('❌ Session ID mismatch:', { 
            expected: sessionId, 
            found: data.sessionId 
          });
          setError('Invalid session ID. Please start a new generation.');
          return;
        }

        // Validate required fields
        if (!data.uploadedImageURL || !data.eventTags) {
          console.error('❌ Missing required data fields:', {
            hasImage: !!data.uploadedImageURL,
            hasEventTags: !!data.eventTags
          });
          setError('Incomplete generation data. Please start a new generation.');
          return;
        }

        console.log('✅ Generation data validation passed');
        setGenerationData(data);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error loading data:', err);
        setError(`Failed to load generation data: ${err.message}`);
      }
    };

    loadUserAndData();
  }, [sessionId, navigate]);

  // Initialize CESDK when data is ready
  useEffect(() => {
    if (!loading && !error && generationData && editorRef.current && !cesdk) {
      console.log('🎨 Initializing CESDK...');
      console.log('📋 Editor container:', editorRef.current);
      console.log('📋 User ID:', user?.id);
      
      initializeCESDK(editorRef.current, {
        userId: user?.id,
        callbacks: {
          onUpload: 'local'
        }
      }).catch(err => {
        console.error('❌ CESDK initialization failed:', err);
        setError(`Editor initialization failed: ${err.message}`);
      });
    }
  }, [loading, error, generationData, cesdk, initializeCESDK, user]);

  // Generate dynamic scene when CESDK is ready (simple version)
  useEffect(() => {
    const generateScene = async () => {
      if (cesdkStatus === 'ready' && generationData && cesdk && !sceneGenerated) {
        try {
          console.log('🎨 Generating scene from image (simple version)...');
          const scene = await cesdk.engine.scene.createFromImage(generationData.uploadedImageURL);
          console.log('✅ Scene created:', scene);

          // Mark this scene as the active scene for the engine/UI.
          try {
            if (typeof cesdk.engine.scene.set === 'function') {
              cesdk.engine.scene.set(scene);
              console.log('✅ Scene set as active via engine.scene.set');
            }
          } catch (setErr) {
            console.warn('⚠️ engine.scene.set failed (maybe unavailable):', setErr);
          }

          // Add text overlays (player + team)
          const pages = cesdk.engine.block.getChildren(scene);
          if (pages && pages.length > 0) {
            const page = pages[0];
            const pageW = cesdk.engine.block.getWidth(page);
            const pageH = cesdk.engine.block.getHeight(page);

            // Player name
            const player = cesdk.engine.block.create('text');
            cesdk.engine.block.setString(player, 'text/text', generationData.eventTags.player_name || 'Player');
            cesdk.engine.block.setFloat(player, 'text/fontSize', 48);
            cesdk.engine.block.setEnum(player, 'text/horizontalAlignment', 'Center');
            cesdk.engine.block.setFloat(player, 'position/x', pageW / 2);
            cesdk.engine.block.setFloat(player, 'position/y', 100);
            cesdk.engine.block.setFloat(player, 'width', pageW * 0.8);
            cesdk.engine.block.setFloat(player, 'height', 80);
            cesdk.engine.block.appendChild(page, player);

            // Team name
            const team = cesdk.engine.block.create('text');
            cesdk.engine.block.setString(team, 'text/text', generationData.eventTags.team_name || 'Team');
            cesdk.engine.block.setFloat(team, 'text/fontSize', 36);
            cesdk.engine.block.setEnum(team, 'text/horizontalAlignment', 'Center');
            cesdk.engine.block.setFloat(team, 'position/x', pageW / 2);
            cesdk.engine.block.setFloat(team, 'position/y', pageH - 100);
            cesdk.engine.block.setFloat(team, 'width', pageW * 0.8);
            cesdk.engine.block.setFloat(team, 'height', 60);
            cesdk.engine.block.appendChild(page, team);
          }

          // Force UI refresh so the editor recognises the new scene.
          if (cesdk.ui) {
            if (typeof cesdk.ui.update === 'function') {
              cesdk.ui.update();
              console.log('✅ UI refreshed via ui.update()');
            } else if (typeof cesdk.ui.unstable_update === 'function') {
              cesdk.ui.unstable_update();
              console.log('✅ UI refreshed via ui.unstable_update()');
            }
          }

          setSceneGenerated(true);
          console.log('🎉 Scene generation complete and activated!');
        } catch (err) {
          console.error('❌ Scene generation error (simple version):', err);
          setError(`Scene generation failed: ${err.message}`);
        }
      }
    };

    generateScene();
  }, [cesdkStatus, cesdk, generationData, sceneGenerated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      // Clear generation data
      sessionStorage.removeItem('dynamicGenerationData');
    };
  }, [cleanup]);

  if (loading) {
    return (
      <VStack spacing={4} align="center" justify="center" minH="80vh">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text>Loading dynamic editor...</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <VStack spacing={4} align="center" justify="center" minH="80vh" maxW="md" mx="auto">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
        <Button colorScheme="blue" onClick={() => navigate('/test-dynamic-generation')}>
          Back to Demo
        </Button>
      </VStack>
    );
  }

  if (!generationData) {
    return (
      <VStack spacing={4} align="center" justify="center" minH="80vh">
        <Text>No generation data found.</Text>
        <Button colorScheme="blue" onClick={() => navigate('/test-dynamic-generation')}>
          Back to Demo
        </Button>
      </VStack>
    );
  }

  return (
    <Box h="100vh" w="100%" position="relative">
      {/* Header */}
      <HStack 
        p={4} 
        bg="white" 
        borderBottom="1px" 
        borderColor="gray.200" 
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={10}
      >
        <VStack align="start" spacing={1} flex={1}>
          <Heading size="md">Dynamic Design Editor</Heading>
          <Text fontSize="sm" color="gray.600">
            {generationData.eventTags.player_name} • {generationData.eventTags.team_name} • {generationData.eventTags.sport}
          </Text>
        </VStack>
        
        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/test-dynamic-generation')}
          >
            Back to Demo
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => {
              // Future: Save design functionality
              alert('Save functionality coming soon!');
            }}
          >
            Save Design
          </Button>
        </HStack>
      </HStack>

      {/* CESDK Editor */}
      <Box 
        ref={editorRef}
        position="absolute"
        top="80px"
        left={0}
        right={0}
        bottom={0}
        bg="#4F6273"
      />

      {/* Loading overlay */}
      {cesdkStatus !== 'ready' && (
        <Box
          position="absolute"
          top="80px"
          left={0}
          right={0}
          bottom={0}
          bg="rgba(255,255,255,0.9)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={20}
        >
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text>Initializing design editor...</Text>
            <Text fontSize="sm" color="gray.600">
              {cesdkStatus === 'initializing' ? 'Loading CESDK...' : 'Preparing your design...'}
            </Text>
            
            {/* Debug Information */}
            <Box p={3} bg="gray.100" borderRadius="md" fontSize="xs" textAlign="left">
              <Text fontWeight="bold" mb={2}>🔧 Debug Info:</Text>
              <Text>• Status: {cesdkStatus}</Text>
              <Text>• Has Editor Ref: {!!editorRef.current ? '✅' : '❌'}</Text>
              <Text>• Has CESDK: {!!cesdk ? '✅' : '❌'}</Text>
              <Text>• Has Generation Data: {!!generationData ? '✅' : '❌'}</Text>
              <Text>• Has User: {!!user ? '✅' : '❌'}</Text>
              <Text>• Scene Generated: {sceneGenerated ? '✅' : '❌'}</Text>
              {generationData && (
                <>
                  <Text>• Image URL: {generationData.uploadedImageURL ? '✅' : '❌'}</Text>
                  <Text>• Event Tags: {generationData.eventTags ? '✅' : '❌'}</Text>
                </>
              )}
            </Box>
            
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                console.log('🔧 Manual Debug Info:');
                console.log('- cesdkStatus:', cesdkStatus);
                console.log('- cesdk:', cesdk);
                console.log('- generationData:', generationData);
                console.log('- user:', user);
                console.log('- editorRef.current:', editorRef.current);
                console.log('- sceneGenerated:', sceneGenerated);
              }}
            >
              Log Debug Info
            </Button>
            
            {/* Timeout Recovery */}
            {initializationTimeout && (
              <Box p={3} bg="orange.100" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="orange.800" mb={2}>
                  ⏰ Initialization is taking longer than expected
                </Text>
                <HStack spacing={2} justify="center">
                  <Button size="sm" colorScheme="orange" onClick={retryInitialization}>
                    Retry
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/test-dynamic-generation')}>
                    Back to Demo
                  </Button>
                </HStack>
              </Box>
            )}
          </VStack>
        </Box>
      )}

      {/* Generation status */}
      {cesdkStatus === 'ready' && !sceneGenerated && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="white"
          p={6}
          borderRadius="lg"
          boxShadow="lg"
          zIndex={30}
        >
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" />
            <Text fontWeight="bold">Generating your design...</Text>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Creating a custom layout with your image and event details
            </Text>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default DynamicCustomizationPage; 